import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../../layouts/PageTitle";
import { useGetAllStaffQuery } from "../../../../store/appApi";
import {
  selectUser,
  selectUserModules,
  selectUserPagePermissions,
} from "../../../../store/authSlice";
import {
  createModemRecoveryEntryApi,
  deleteModemRecoveryEntryApi,
  getModemRecoveryEntriesApi,
  updateModemRecoveryEntryApi,
} from "../../../../services/modemRecovery";
import {
  formatDateLabel,
  monthLabel,
  monthValue,
  MOVEMENTS,
  num,
  previewClosing,
  toDateInputValue,
} from "./config";

const PAGE_KEY = "workforce.modemRecovery";
const LEVELS = { none: 0, read: 1, "read-write": 2 };

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 disabled:bg-gray-50 disabled:text-gray-500";
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const emptyMovements = () =>
  Object.fromEntries(MOVEMENTS.map((m) => [m.key, ""]));

const ModemRecoveryList = () => {
  const me = useSelector(selectUser);
  const modules = useSelector(selectUserModules);
  const pagePermissions = useSelector(selectUserPagePermissions);
  const isSuperAdmin = me?.role === "super-admin";

  const explicitPageLevel = pagePermissions?.[PAGE_KEY];
  const effectiveLevel =
    explicitPageLevel && explicitPageLevel !== "inherit"
      ? explicitPageLevel
      : (modules?.workforce ?? "none");
  const canView = isSuperAdmin || (LEVELS[effectiveLevel] ?? 0) >= LEVELS.read;
  const canWrite =
    isSuperAdmin || (LEVELS[effectiveLevel] ?? 0) >= LEVELS["read-write"];

  // Admin: pick ANY employee's ledger to view/manage — access to Modem
  // Recovery is granted per person, but the admin themselves isn't limited
  // to only those already granted (they may be setting one up for the
  // first time).
  const { data: staffData } = useGetAllStaffQuery(
    { page: 1, limit: 500 },
    { skip: !isSuperAdmin },
  );
  const allStaff = useMemo(
    () =>
      (Array.isArray(staffData?.staff) ? staffData.staff : [])
        .filter((s) => s?.status !== false)
        .sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [staffData],
  );
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  const employeeId = isSuperAdmin ? selectedEmployeeId : "";
  const viewingLabel = isSuperAdmin
    ? allStaff.find((s) => String(s._id) === String(selectedEmployeeId))?.name
    : me?.name;

  const [month, setMonth] = useState(() => monthValue());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasOpening, setHasOpening] = useState(null); // null = unknown yet

  const canLoad = canView && (!isSuperAdmin || Boolean(employeeId));

  const reload = () => {
    if (!canLoad) {
      setEntries([]);
      return;
    }
    setLoading(true);
    getModemRecoveryEntriesApi({ employeeId: employeeId || undefined, month })
      .then((res) => {
        setEntries(res?.data?.entries || []);
        const latest = res?.data?.latest;
        setHasOpening(Boolean(latest));
      })
      .catch(() => {
        setEntries([]);
        setHasOpening(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(reload, [canLoad, employeeId, month]);

  const sorted = useMemo(
    () => [...entries].sort((a, b) => new Date(a.date) - new Date(b.date)),
    [entries],
  );

  // ── Entry modal (opening declaration, or a day's movements) ───────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // null = new
  const [draftDate, setDraftDate] = useState(toDateInputValue());
  const [draftOpening, setDraftOpening] = useState("");
  const [draftMovements, setDraftMovements] = useState(emptyMovements());
  const [draftNote, setDraftNote] = useState("");
  const [saving, setSaving] = useState(false);

  // The entry already on today's date, if any.
  const todaysEntry = useMemo(
    () =>
      sorted.find(
        (e) => toDateInputValue(e.date) === toDateInputValue(),
      ) || null,
    [sorted],
  );

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setDraftDate(toDateInputValue(entry.date));
    setDraftOpening(entry.isOpening ? String(entry.opening ?? "") : "");
    setDraftMovements(
      Object.fromEntries(
        MOVEMENTS.map((m) => [m.key, String(entry[m.key] ?? "")]),
      ),
    );
    setDraftNote(entry.note || "");
    setModalOpen(true);
  };

  const openNewEntry = () => {
    // If today already has a row (e.g. the opening entry was just declared
    // for today), open THAT for editing instead of a blocked "new" modal.
    if (todaysEntry) {
      openEditEntry(todaysEntry);
      return;
    }
    setEditingEntry(null);
    setDraftDate(toDateInputValue());
    setDraftOpening("");
    setDraftMovements(emptyMovements());
    setDraftNote("");
    setModalOpen(true);
  };

  const isOpeningEntry = editingEntry ? editingEntry.isOpening : !hasOpening;
  // A new entry can't reuse an existing day (backend rejects it with 409).
  const dateClash =
    !editingEntry &&
    !!draftDate &&
    sorted.some((e) => toDateInputValue(e.date) === draftDate);
  // Base to carry forward from: the opening entry starts from its typed opening
  // balance; every later day starts from the previous close.
  const carryFrom = isOpeningEntry
    ? num(draftOpening)
    : sorted[sorted.length - 1]?.closing ?? 0;
  const previewedClosing = previewClosing(carryFrom, draftMovements);

  const handleSave = async () => {
    const payload = {
      date: draftDate,
      note: draftNote,
      ...(employeeId ? { employeeId } : {}),
    };
    // Movements go on every entry (the opening day included).
    MOVEMENTS.forEach((m) => {
      payload[m.key] = num(draftMovements[m.key]);
    });
    if (isOpeningEntry) {
      payload.opening = num(draftOpening);
    }
    try {
      setSaving(true);
      if (editingEntry) {
        await updateModemRecoveryEntryApi(editingEntry._id, payload);
      } else {
        await createModemRecoveryEntryApi(payload);
      }
      setModalOpen(false);
      reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not save",
        text: error?.response?.data?.message || "The entry could not be saved.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete this entry?",
      text: entry.isOpening
        ? "This is the opening entry — it can only be deleted once it's the only entry left."
        : `Removes ${formatDateLabel(entry.date)}'s entry.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteModemRecoveryEntryApi(entry._id, employeeId || undefined);
      reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not delete",
        text: error?.response?.data?.message || "The entry could not be removed.",
      });
    }
  };

  const monthTotals = useMemo(() => {
    if (!sorted.length) return null;
    const sum = (key) => sorted.reduce((acc, e) => acc + num(e[key]), 0);
    return {
      opening: num(sorted[0].opening),
      closing: num(sorted[sorted.length - 1].closing),
      ...Object.fromEntries(MOVEMENTS.map((m) => [m.key, sum(m.key)])),
    };
  }, [sorted]);

  return (
    <>
      <PageTitle activeMenu="Modem Recovery" motherMenu="Workforce" />
      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Netplus
              </div>
              <h1 className="mt-3 text-2xl font-semibold text-gray-900">
                Modem Recovery
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Your personal running balance of pending modem-recovery cases.
                Declare your opening count once, then log each day's Recovered,
                Recharged/Retained, In Hand, Untraceable and New Cases Added —
                the balance carries forward automatically.
              </p>
            </div>
            {isSuperAdmin && (
              <div className="w-64">
                <label className={labelClass}>Employee</label>
                <select
                  className={inputClass}
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                >
                  <option value="">Select an employee…</option>
                  {allStaff.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name} · {s.role}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  As admin you can set up or manage anyone's ledger here,
                  whether or not they've been granted the permission yet.
                </p>
              </div>
            )}
          </div>
        </section>

        {!canView ? (
          <section className={`${panelClass} p-8 text-center text-gray-500`}>
            You don't have access to Modem Recovery. Ask an admin to grant it
            from Edit Staff → Workforce → "Staff Reporting — Modem Recovery."
          </section>
        ) : isSuperAdmin && !selectedEmployeeId ? (
          <section className={`${panelClass} p-8 text-center text-gray-500`}>
            Pick an employee above to view their ledger.
          </section>
        ) : (
          <>
            <section className={`${panelClass} p-5 md:p-6`}>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <label className={labelClass} style={{ marginBottom: 0 }}>
                    Month
                  </label>
                  <input
                    type="month"
                    className={`${inputClass} w-44`}
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  />
                  <span className="text-sm text-gray-500">
                    {monthLabel(month)}
                    {viewingLabel ? ` · ${viewingLabel}` : ""}
                  </span>
                </div>
                {canWrite && (
                  <button
                    type="button"
                    className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={openNewEntry}
                    disabled={loading || hasOpening === null}
                  >
                    {!hasOpening
                      ? "Set Opening Balance & First Day"
                      : todaysEntry
                        ? "Edit Today's Entry"
                        : "+ Add Today's Entry"}
                  </button>
                )}
              </div>

              {monthTotals && (
                <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                  {[
                    ["Opening", monthTotals.opening],
                    ...MOVEMENTS.map((m) => [m.label, monthTotals[m.key]]),
                    ["Closing", monthTotals.closing],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3 text-center"
                    >
                      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                        {label}
                      </div>
                      <div className="mt-1 text-lg font-semibold text-gray-900">
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-medium">
                        Date
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
                        Opening
                      </th>
                      {MOVEMENTS.map((m) => (
                        <th
                          key={m.key}
                          className="whitespace-nowrap px-3 py-2 text-right font-medium"
                        >
                          {m.label}
                        </th>
                      ))}
                      <th className="whitespace-nowrap px-3 py-2 text-right font-medium">
                        Closing
                      </th>
                      <th className="px-3 py-2 text-left font-medium">Note</th>
                      {canWrite && (
                        <th className="px-3 py-2 text-left font-medium">·</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {!sorted.length && !loading && (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-3 py-6 text-center text-xs text-gray-400"
                        >
                          {hasOpening === false
                            ? "No opening balance set for this ledger yet."
                            : "No entries for this month."}
                        </td>
                      </tr>
                    )}
                    {sorted.map((e) => (
                      <tr key={e._id} className={e.isOpening ? "bg-amber-50/40" : undefined}>
                        <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900">
                          {formatDateLabel(e.date)}
                          {e.isOpening && (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                              Opening
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right text-gray-600">
                          {num(e.opening)}
                        </td>
                        {MOVEMENTS.map((m) => (
                          <td
                            key={m.key}
                            className="whitespace-nowrap px-3 py-2 text-right text-gray-600"
                          >
                            {num(e[m.key])}
                          </td>
                        ))}
                        <td className="whitespace-nowrap px-3 py-2 text-right font-semibold text-gray-900">
                          {num(e.closing)}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{e.note || "—"}</td>
                        {canWrite && (
                          <td className="whitespace-nowrap px-3 py-2">
                            <button
                              type="button"
                              className="mr-2 text-xs font-semibold text-sky-700 hover:underline"
                              onClick={() => openEditEntry(e)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="text-xs font-semibold text-rose-600 hover:underline"
                              onClick={() => handleDelete(e)}
                            >
                              Delete
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !saving && setModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">
              {editingEntry
                ? `Edit ${formatDateLabel(editingEntry.date)}`
                : isOpeningEntry
                  ? "Opening balance & first day"
                  : "Add today's entry"}
            </h3>

            <div className="mt-4 space-y-3">
              <div>
                <label className={labelClass}>Date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={draftDate}
                  max={toDateInputValue()}
                  onChange={(e) => setDraftDate(e.target.value)}
                />
              </div>
              {dateClash && (
                <p className="text-xs font-medium text-rose-600">
                  An entry for {formatDateLabel(draftDate)} already exists —
                  pick another date, or close this and use its Edit button.
                </p>
              )}

              {isOpeningEntry && (
                <div>
                  <label className={labelClass}>Opening balance</label>
                  <input
                    type="number"
                    min="0"
                    className={inputClass}
                    placeholder="Pending cases to start with"
                    value={draftOpening}
                    onChange={(e) => setDraftOpening(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    You can also log this day's movements below — the closing
                    balance carries forward from here.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {MOVEMENTS.map((m) => (
                  <div key={m.key}>
                    <label className={labelClass} title={m.hint}>
                      {m.label}
                    </label>
                    <input
                      type="number"
                      min="0"
                      className={inputClass}
                      placeholder="0"
                      value={draftMovements[m.key]}
                      onChange={(e) =>
                        setDraftMovements((prev) => ({
                          ...prev,
                          [m.key]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className={labelClass}>Note (optional)</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value)}
                />
              </div>

              <div className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-600">
                Closing balance after this entry:{" "}
                <span className="font-semibold text-gray-900">
                  {previewedClosing}
                </span>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleSave}
                disabled={saving || !draftDate || dateClash}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModemRecoveryList;
